import { createContext, useContext, useState } from "react";
import { Link } from "react-router-dom";
import { daDate, diffDays, toDate } from "@/lib/date";
import { ST_BADGE } from "@/components/le/ActionUI";
import { OriginLine } from "@/components/le/FollowupCard";
import { ChevronRight } from "lucide-react";

export const PrintCtx = createContext(false);
export const INK = "#1A1919", MUTED = "#78716C", FAINT = "#A8A29E", BROWN = "#806B61", RED = "#A83232", LINE = "#EBE8E3";

export const overdueDays = (deadline, ref = new Date()) => {
  const d = toDate(deadline); if (!d) return 0;
  return Math.max(1, diffDays(ref, d));
};

export function MStatusBadge({ status, overdue, deadline, closedAt, testid }) {
  if (overdue) return <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#F1DEDA] text-[#A83232]" data-testid={testid}>Overskredet · {overdueDays(deadline)} dage</span>;
  if (status === "Lukket") return <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#E7EADF] text-[#5f6b4a]" data-testid={testid}>Lukket{closedAt ? ` ${daDate(closedAt).slice(0, 5)}` : ""}</span>;
  return <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${ST_BADGE[status] || "bg-stone-100 text-stone-600 border-stone-200"}`} data-testid={testid}>{status}</span>;
}

export const MLabel = ({ children, red = false, className = "" }) => (
  <p className={`text-[11px] font-semibold uppercase tracking-[0.14em] flex items-center gap-2 ${className}`} style={{ color: red ? RED : BROWN }}>{red && <span className="h-1.5 w-1.5 rounded-full" style={{ background: RED }} />}{children}</p>
);

export function MCollapse({ label, count, children, testid, defaultOpen = false }) {
  const [openState, setOpen] = useState(defaultOpen);
  const printing = useContext(PrintCtx);
  const open = openState || printing;
  if (printing) return (
    <div className="mt-6" data-testid={testid}>
      <MLabel className="mb-3">{label.replace(/^Vis\s+/i, "")}{count != null ? ` (${count})` : ""}</MLabel>
      <div className="space-y-3">{children}</div>
    </div>
  );
  return (
    <div className="mt-5" data-testid={testid}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex items-center gap-1 text-sm font-semibold hover:text-stone-900 transition-colors" style={{ color: "#57534E" }} data-testid={testid ? `${testid}-toggle` : undefined}>
        <ChevronRight className={`h-3.5 w-3.5 transition-transform mv-chevron ${open ? "rotate-90" : ""}`} /> {label}{count != null ? ` (${count})` : ""}
      </button>
      {open && <div className="mt-3 space-y-3">{children}</div>}
    </div>
  );
}

export function MPoint({ id, num, title, meta, empty = false, emptyText = "intet til dette punkt", children, testid }) {
  if (empty) return (
    <section id={id} className="scroll-mt-24 mt-12 md:mt-14 pt-8 border-t print-avoid" style={{ borderColor: LINE }} data-testid={testid}>
      <div className="flex items-baseline gap-3 flex-wrap">
        <span className="font-mono text-sm" style={{ color: FAINT }}>{num}</span>
        <h2 className="text-xl md:text-2xl font-semibold tracking-tight" style={{ color: MUTED }}>{title}</h2>
        <span className="text-sm md:text-base" style={{ color: FAINT }}>– {emptyText}</span>
      </div>
    </section>
  );
  return (
    <section id={id} className="scroll-mt-24 mt-12 md:mt-16 pt-10 border-t" style={{ borderColor: LINE }} data-testid={testid}>
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div className="flex items-baseline gap-3"><span className="font-mono text-sm" style={{ color: FAINT }}>{num}</span><h2 className="text-2xl md:text-3xl font-semibold tracking-tight" style={{ color: INK }}>{title}</h2></div>
        {meta && <span className="text-sm" style={{ color: MUTED }}>{meta}</span>}
      </div>
      <div className="mt-5">{children}</div>
    </section>
  );
}

export const MCard = ({ children, className = "", ...p }) => (
  <div {...p} className={`bg-white rounded-2xl border p-4 md:p-5 print-avoid ${className}`} style={{ borderColor: LINE }}>{children}</div>
);

export function MActionCard({ action: a, right = null, showOrigin = false, noLink = true, testid }) {
  const closed = a.status === "Lukket";
  const latest = a.latest_followup;
  const title = <p className="text-[15px] md:text-base font-semibold mt-1.5" style={{ color: INK }}>{a.description}</p>;
  return (
    <MCard data-testid={testid || `mac-${a.id}`}>
      <div className="flex items-center gap-2.5 flex-wrap text-xs">
        <span className="font-mono" style={{ color: MUTED }}>{a.action_no}</span>
        <MStatusBadge status={a.status} overdue={a.overdue && !closed} deadline={a.deadline} closedAt={a.closed_at} />
        {right && <span className="ml-auto text-xs" style={{ color: MUTED }}>{right}</span>}
      </div>
      {noLink ? title : <Link to={`/handling/${a.id}`} className="block hover:text-[#A83232]">{title}</Link>}
      {!closed && <p className="text-sm mt-1" style={{ color: MUTED }}>Ansvarlig: {a.responsible_name || "—"} · Frist {a.deadline ? daDate(a.deadline) : "—"}</p>}
      {showOrigin && <OriginLine action={a} className="mt-0.5" />}
      {closed && (a.closing_comment || a.latest_followup?.comment) && (
        <p className="text-sm mt-3 pt-3 border-t" style={{ borderColor: "#F0EDE8", color: "#44403C" }}><span style={{ color: MUTED }}>Løsning: </span>{a.closing_comment || a.latest_followup?.comment}</p>
      )}
      {!closed && latest && (latest.comment || latest.status_to) && (
        <p className="text-sm mt-3 pt-3 border-t" style={{ borderColor: "#F0EDE8", color: "#44403C" }}><span style={{ color: MUTED }}>Seneste opfølgning {daDate(latest.at).slice(0, 5)}: </span>{latest.comment || `Status → ${latest.status_to}`}</p>
      )}
    </MCard>
  );
}

export function MCompactRows({ rows, testid }) {
  return (
    <div className="bg-white rounded-2xl border divide-y overflow-hidden" style={{ borderColor: LINE }} data-testid={testid}>
      {rows.map((r) => (
        <div key={r.id} className="flex items-center gap-3 px-4 py-3 flex-wrap print-avoid" style={{ borderColor: "#F0EDE8" }} data-testid={`mrow-${r.id}`}>
          <span className="font-mono text-xs shrink-0" style={{ color: MUTED }}>{r.no}</span>
          <span className="text-sm md:text-[15px] font-semibold flex-1 min-w-[12rem]" style={{ color: INK }}>{r.title}</span>
          <MStatusBadge status={r.status} overdue={r.overdue} deadline={r.deadline} />
          <span className="text-sm font-mono w-12 text-right" style={{ color: MUTED }}>{r.deadline ? daDate(r.deadline).slice(0, 5) : "—"}</span>
        </div>
      ))}
    </div>
  );
}

export function MStat({ value, label, red = false, testid }) {
  return (
    <div className="bg-white rounded-2xl border px-5 py-4" style={{ borderColor: LINE }} data-testid={testid}>
      <p className="text-3xl font-semibold tracking-tight" style={{ color: red && value > 0 ? RED : INK }}>{value}</p>
      <p className="text-sm mt-0.5" style={{ color: MUTED }}>{label}</p>
    </div>
  );
}

// Pkt. 6 – "Sket siden sidst" / "Lukket": én kompakt række pr. handling/fund med HVAD der skete
const EV_KIND_LABEL = { created: "Oprettet", followup: "Opfølgning", status: "Status", closed: "Lukket", reopen: "Genåbnet" };
const one = (s, n = 110) => { const t = String(s || "").replace(/\s+/g, " ").trim(); return t.length > n ? `${t.slice(0, n - 1)}…` : t; };
export function sinceWhat(a) {
  const evs = (a.since_events || []).filter((e) => e.kind !== "created" || !a.since_events.some((x) => x.kind !== "created"));
  const parts = [];
  const st = [...evs].reverse().find((e) => e.kind === "status" && e.status_to);
  if (st) parts.push(`${st.status_from || "—"} → ${st.status_to}`);
  const last = [...evs].reverse().find((e) => e.comment) || a.latest_followup;
  if (last?.comment) parts.push(`${daDate(last.at).slice(0, 5)}: ${one(last.comment)}`);
  if (!parts.length) { const e = evs[evs.length - 1]; if (e) parts.push(`${daDate(e.at).slice(0, 5)}: ${EV_KIND_LABEL[e.kind] || e.kind}${e.outside_le ? " uden for LE" : ""}${e.in_meeting ? " på mødet" : ""}`); }
  return parts.join(" · ");
}
export function SinceRows({ items, closed = false, onOpen, testid }) {
  if (!items?.length) return null;
  return (
    <div className="bg-white rounded-2xl border divide-y overflow-hidden" style={{ borderColor: LINE }} data-testid={testid}>
      {items.map((a) => {
        const what = closed ? (a.closing_comment || a.latest_followup?.comment || (a.since_events || []).filter((e) => e.kind === "closed").map((e) => e.comment).find(Boolean)) : sinceWhat(a);
        const Row = onOpen ? "button" : "div";
        return (
          <Row key={a.id} type={onOpen ? "button" : undefined} onClick={onOpen ? () => onOpen(a) : undefined} className={`w-full text-left flex flex-wrap items-start gap-x-3 gap-y-1 px-4 py-2.5 print-avoid ${onOpen ? "hover:bg-[#FBFAF8] transition-colors" : ""}`} style={{ borderColor: "#F0EDE8" }} data-testid={`since-row-${a.id}`}>
            <span className="font-mono text-xs shrink-0 pt-0.5" style={{ color: MUTED }}>{a.action_no || a.finding_no}</span>
            <div className="flex-1 min-w-[10rem]">
              <p className="text-sm font-semibold line-clamp-2" style={{ color: INK }}>{a.description}</p>
              {what && <p className="text-sm line-clamp-2" style={{ color: "#57534E" }}>{closed ? <><span style={{ color: MUTED }}>Løsning: </span>{one(what, 180)}</> : what}</p>}
            </div>
            {closed ? <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#E7EADF] text-[#5f6b4a] shrink-0 max-sm:order-last max-sm:ml-auto">Lukket{a.closed_at ? ` ${daDate(a.closed_at).slice(0, 5)}` : ""}</span>
              : <span className="shrink-0 max-sm:order-last max-sm:ml-auto"><MStatusBadge status={a.status} overdue={a.overdue && a.status !== "Lukket"} deadline={a.deadline} /></span>}
          </Row>
        );
      })}
    </div>
  );
}
